import {
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { trackDeleteByRangeEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import { removeDestroyedRestDaysCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedRestDaysCache'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'

interface Props {
  startDate: Date
  endDate: Date
  employeeSelection: EmployeesEmployeeSelectorInput
  destroyBackupShifts: boolean
  destroyAutomaticOnly: boolean
  onClose: () => void
}

const useDestroyByRange = ({
  startDate,
  endDate,
  employeeSelection,
  destroyBackupShifts,
  destroyAutomaticOnly,
  onClose,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const { refreshStats } = useRefreshStats()
  const restDayFlag = hasFeatureFlag('shift_management_rest_day')

  const startDateParam = i18nDateToString(startDate, 'backend')
  const endDateParam = i18nDateToString(endDate, 'backend')

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      removeDestroyedShiftsCache({
        cache,
        view,
        shifts: data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts || [],
      })
    },
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
    update: (cache, { data }) => {
      removeDestroyedRestDaysCache({
        cache,
        view,
        restDays: data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays || [],
      })
    },
  })

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      const result = await destroyShifts({
        variables: {
          startAt: startDateParam,
          endAt: endDateParam,
          employeeSelection,
          destroyBackupShifts: destroyBackupShifts,
          destroyAutomaticShifts: destroyAutomaticOnly,
        },
      })

      const handled = handleResult(result, (_errors) => {})

      const shiftsData = handled.success ? handled.data?.shifts || [] : []
      const deletedShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId ?? '0',
        })
      )

      const deleteRestDays = async () => {
        if (!restDayFlag) return []
        const restDaysResult = await destroyRestDays({
          variables: {
            startAt: startDateParam,
            endAt: endDateParam,
            employeeSelection,
          },
        })

        const handledRestDays = handleResult(restDaysResult, (_errors) => {})
        const restDays = handledRestDays.success ? handledRestDays.data?.restDays || [] : []
        return restDays.map((restDay) => {
          return RestDay.build({
            id: restDay.id,
            employee_id: restDay.employee?.id ?? '0',
            date: restDay.date,
            state: restDay.state,
            timezone: restDay.timezone || '',
            employeeFullName: restDay.employee?.fullName || '',
            employeeAvatar: restDay.employee?.avatar?.url || '',
          })
        })
      }

      const deletedRestDays = await deleteRestDays()

      trackDeleteByRangeEvent(
        deletedShifts,
        deletedRestDays,
        view,
        startDateParam,
        endDateParam,
        destroyAutomaticOnly
      )

      await refreshStats(shiftsRows)

      onClose()
    },
    {
      errorFlash: true,
      successMessage: i18n.t('shift_management.actions.delete_shifts_success'),
    }
  )

  return {
    onBulkDestroyShifts: performAction,
    submitting,
  }
}

export default useDestroyByRange
